import { Map, Sprite, Furniture, Table, MapResizeMode } from './Map';
import { Xml, XmlElement } from 'core/utils';
import { MapImageCache } from './MapImageCache';
import Transformation from './Transformation';


function getAttributeValue(element: Element, localName: string): string {
	const value = element.getAttribute(localName);
	if (!value)
		throw Error('No existe el attributo ' + localName + ' para el elemento ' + element.localName);
	return value;
}

function getAttributeIntValue(element: Element, name: string): number {
	return parseInt(getAttributeValue(element, name));
}

function parseFurniture(element: Element, cache: MapImageCache): Sprite {

	const x = getAttributeIntValue(element, 'x');
	const y = getAttributeIntValue(element, 'y');
	const rotation = getAttributeIntValue(element, 'rotation');
	const deltaX = getAttributeIntValue(element, 'deltaX');
	const deltaY = getAttributeIntValue(element, 'deltaY');
	const imageSrc = getAttributeValue(element, 'image');

	return new Furniture({ x, y }, new Transformation(rotation, deltaX, deltaY), cache.getImage(imageSrc));
}

function parseTable(element: Element, cache: MapImageCache): Table {

	const x = getAttributeIntValue(element, 'x');
	const y = getAttributeIntValue(element, 'y');
	const rotation = getAttributeIntValue(element, 'rotation');
	const deltaX = getAttributeIntValue(element, 'deltaX');
	const deltaY = getAttributeIntValue(element, 'deltaY');
	const saleCenterId = getAttributeIntValue(element, 'saleCenterId');
	const saleLocationName = getAttributeValue(element, 'saleLocationName');
	const imageSrc = getAttributeValue(element, 'image');

	return new Table({ x, y }, new Transformation(rotation, deltaX, deltaY), cache.getImage(imageSrc), { saleCenterId, saleLocationName });
}

export async function toMap(xml: string, cache: MapImageCache, validLocations: Array<{ SaleCenterId: number, SaleLocationName: string }>) {

	const parser = new DOMParser();
	const doc = parser.parseFromString(xml, 'application/xml');

	const sizeElement = doc.getElementsByTagName('size')[0];

	const backgroundElement = doc.getElementsByTagName('background')[0];
	const customBackgroundElement = doc.getElementsByTagName('customBackground')[0];

	const backgroundImage = customBackgroundElement?.textContent
		? await cache.getCustom(customBackgroundElement.textContent)
		: cache.getImage(backgroundElement?.textContent ?? '');

	const backgroundType = customBackgroundElement?.textContent ? 'CUSTOM' : 'PREDEFINED';

	const width = parseInt(sizeElement.getAttribute('width') || '400');
	const height = parseInt(sizeElement.getAttribute('height') || '400');
	const resizeMode = sizeElement.getAttribute('resizeMode') as MapResizeMode || 'shrink';

	const tableElements = doc.getElementsByTagName('table');
	const furnitureElements = doc.getElementsByTagName('furniture');

	const isStillValid = (table: Table): boolean =>
		validLocations.some(x => x.SaleCenterId === table.location.saleCenterId && x.SaleLocationName === table.location.saleLocationName);

	const tables: Sprite[] = Array.from(tableElements)
		.map(x => parseTable(x, cache))
		.filter(isStillValid);

	const furnitures = Array.from(furnitureElements)
		.map(x => parseFurniture(x, cache));

	return new Map(
		width,
		height,
		resizeMode,
		tables.concat(furnitures),
		backgroundImage,
		backgroundType);
}

export function toXml(map: Map): string {



	const rootElement: XmlElement = {
		name: 'map',
		children: [
			{
				name: 'size', attributes: [
					{ name: 'width', value: Math.round(map.width).toString() },
					{ name: 'height', value: Math.round(map.height).toString() },
					{ name: 'resizeMode', value: map.resizeMode ?? 'shrink' },
				]
			},
			{
				name: map.backgroundType === 'CUSTOM' ? 'customBackground' : 'background',
				value: map.backgroundImage.name
			},
			{
				name: 'tables', children: map.sprites.filter(s => s instanceof Table).map(x => x as Table).map((t: Table) => ({
					name: 'table', attributes: [
						{ name: 'x', value: Math.round(t.point.x).toString() },
						{ name: 'y', value: Math.round(t.point.y).toString() },
						{ name: 'saleCenterId', value: t.location.saleCenterId.toString() },
						{ name: 'saleLocationName', value: t.location.saleLocationName },
						{ name: 'image', value: t.image.name },
						{ name: 'rotation', value: t.transformation.rotation.toString() },
						{ name: 'deltaX', value: Math.round(t.transformation.deltaX).toString() },
						{ name: 'deltaY', value: Math.round(t.transformation.deltaY).toString() },
					]
				}))
			},
			{
				name: 'furnitures', children: map.sprites.filter(s => s instanceof Furniture).map(t => ({
					name: 'furniture', attributes: [
						{ name: 'x', value: Math.round(t.point.x).toString() },
						{ name: 'y', value: Math.round(t.point.y).toString() },
						{ name: 'image', value: t.image.name },
						{ name: 'rotation', value: t.transformation.rotation.toString() },
						{ name: 'deltaX', value: Math.round(t.transformation.deltaX).toString() },
						{ name: 'deltaY', value: Math.round(t.transformation.deltaY).toString() },
					]
				}))
			}
		]
	};

	const xml = Xml.serialize(rootElement);
	return xml;
}
